'use client';

import { c as _c } from "react/compiler-runtime";
import { hashDiagnosticEntity, setWalletAssetEntity, useAuth, useGetProfilePortfolioSnapshot, useQueries, useQuery, useQueryClient, type WalletAssetEntity, walletAssetEntityObserverOptions } from '@morfi/backend-sdk-v2/client';
import type { FundingWalletRefresh } from '@morfi/onramp-sdk/react';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import { D6_SCALE, getUsdcMint } from '@/config/solana';
import type { Network } from '@/lib/network/types';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { parseTokenAccountBalance } from '@/lib/solana/ata';
import { reportError } from '@/lib/telemetry/client';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
export const WALLET_BALANCE_QUERY_KEY = 'wallet-balance';
interface WalletBalanceRequestMetadata {
  readonly addresses: readonly string[];
  readonly fetchedAtMs: number;
  readonly slot: string;
}
interface FetchedWalletBalances extends WalletBalanceRequestMetadata {
  readonly entities: readonly WalletAssetEntity[];
}
async function fetchBatchBalances(addresses: readonly string[], network: Network): Promise<FetchedWalletBalances> {
  const connection = getConnection(network);
  const mint = getUsdcMint(network);
  const usdcMint = new PublicKey(mint);
  const fetchedAtMs = Date.now();
  const rawBalances = new Map<string, string>();
  const ataKeys: PublicKey[] = [];
  const validAddresses: string[] = [];
  for (const address of addresses) {
    try {
      const walletPubkey = new PublicKey(address);
      ataKeys.push(getAssociatedTokenAddressSync(usdcMint, walletPubkey));
      validAddresses.push(address);
    } catch (error: unknown) {
      reportError(error, {
        component: 'fetchBatchBalances',
        network,
        walletHash: hashDiagnosticEntity(address),
        mintHash: hashDiagnosticEntity(mint)
      });
      rawBalances.set(address, '0');
    }
  }
  const response = ataKeys.length === 0 ? {
    context: {
      slot: 0
    },
    value: []
  } : await connection.getMultipleAccountsInfoAndContext(ataKeys);
  for (let index = 0; index < validAddresses.length; index += 1) {
    const address = validAddresses[index];
    if (address === undefined) continue;
    const account = response.value[index];
    rawBalances.set(address, account?.data === undefined ? '0' : parseTokenAccountBalance(account.data).toString());
  }
  const slot = response.context.slot.toString();
  return {
    addresses,
    fetchedAtMs,
    slot,
    entities: addresses.map((wallet): WalletAssetEntity => ({
      network,
      wallet,
      mint,
      rawBalance: rawBalances.get(wallet) ?? '0',
      fetchedAtMs,
      slot,
      source: 'rpc'
    }))
  };
}
export interface WalletBalancesResult {
  /** Per-wallet display-dollar balances, keyed by address. */
  perWallet: Map<string, number>;
  /** Per-wallet raw u64 D6 balances, keyed by address. */
  perWalletRaw: Map<string, bigint>;
  /** Sum of all per-wallet balances in display dollars. */
  total: number;
  /** True while the batched fetch is in progress for any address. */
  isLoading: boolean;
  /** True if the fetch errored. */
  isError: boolean;
}
export function useInvalidateWalletBalances() {
  const $ = _c(3);
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  let t0;
  if ($[0] !== network || $[1] !== queryClient) {
    t0 = walletAddress => {
      if (walletAddress === undefined) {
        queryClient.invalidateQueries({
          queryKey: [WALLET_BALANCE_QUERY_KEY]
        });
        return;
      }
      const batchQueries = queryClient.getQueryCache().findAll({
        queryKey: [WALLET_BALANCE_QUERY_KEY, "batch", network]
      });
      for (const query of batchQueries) {
        const addressSet = query.queryKey[3];
        if (typeof addressSet === "string" && addressSet.split(",").includes(walletAddress)) {
          queryClient.invalidateQueries({
            queryKey: query.queryKey,
            exact: true
          });
        }
      }
    };
    $[0] = network;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function useWalletBalances(addresses: string[]): WalletBalancesResult {
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  const mint = getUsdcMint(network);
  const normalizedAddresses = [...new Set(addresses)].sort();
  const request = useQuery<WalletBalanceRequestMetadata, Error>({
    queryKey: [WALLET_BALANCE_QUERY_KEY, 'batch', network, normalizedAddresses.join(',')],
    queryFn: async (): Promise<WalletBalanceRequestMetadata> => {
      try {
        const fetched = await fetchBatchBalances(normalizedAddresses, network);
        for (const entity of fetched.entities) {
          setWalletAssetEntity(queryClient, entity, logger);
        }
        return {
          addresses: fetched.addresses,
          fetchedAtMs: fetched.fetchedAtMs,
          slot: fetched.slot
        };
      } catch (error: unknown) {
        reportError(error, {
          component: 'useWalletBalances',
          network,
          walletSetHash: hashDiagnosticEntity(normalizedAddresses.join(',')),
          mintHash: hashDiagnosticEntity(mint)
        });
        throw error;
      }
    },
    enabled: normalizedAddresses.length > 0,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true
  });
  const entityQueries = useQueries({
    queries: normalizedAddresses.map(wallet => walletAssetEntityObserverOptions(queryClient, {
      network,
      wallet,
      mint
    }))
  });
  const perWallet = new Map<string, number>();
  const perWalletRaw = new Map<string, bigint>();
  let total = 0;
  for (let index = 0; index < normalizedAddresses.length; index += 1) {
    const address = normalizedAddresses[index];
    const entity_0 = entityQueries[index]?.data as WalletAssetEntity | undefined;
    if (address === undefined || entity_0 === undefined) continue;
    const rawBalance = BigInt(entity_0.rawBalance);
    const displayBalance = Number(rawBalance) / D6_SCALE;
    perWallet.set(address, displayBalance);
    perWalletRaw.set(address, rawBalance);
    total += displayBalance;
  }
  return {
    perWallet,
    perWalletRaw,
    total,
    isLoading: normalizedAddresses.length === 0 ? false : request.isLoading,
    isError: normalizedAddresses.length === 0 ? false : request.isError
  };
}

/** Refresh display authority, not an inferred balance delta from a payment callback. */
export function useRefreshPortfolioWallet() {
  const $ = _c(10);
  const {
    user,
    isAuthenticated
  } = useAuth();
  const t0 = user?.id ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      userId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      enabled: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const snapshot = useGetProfilePortfolioSnapshot(t1, t2);
  const t3 = useActiveNetwork();
  let t4;
  if ($[3] !== t3) {
    t4 = getUsdcMint(t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const mint = t4;
  let t5;
  if ($[5] !== isAuthenticated || $[6] !== mint || $[7] !== snapshot || $[8] !== user) {
    t5 = async t6 => {
      const {
        destination,
        mint: outcomeMint,
        signal
      } = t6;
      signal.throwIfAborted();
      if (!isAuthenticated || !user) {
        throw new Error("onramp_wallet_refresh_unauthenticated");
      }
      const result = await snapshot.refetch();
      signal.throwIfAborted();
      const observed = outcomeMint ? result?.positions.find(row => row.wallet === destination && row.outcomeMint === outcomeMint) : result?.quoteBalances.find(row_0 => row_0.wallet === destination && row_0.quoteMint === mint);
      if (!result?.wallets.includes(destination) || !observed) {
        logger.warn("onramp.wallet_refresh_failed", {
          reason: !result ? "snapshot_unavailable" : "destination_unobserved"
        });
        throw new Error("onramp_wallet_refresh_unavailable");
      }
      return {
        destination,
        observedAtMs: Date.now()
      };
    };
    $[5] = isAuthenticated;
    $[6] = mint;
    $[7] = snapshot;
    $[8] = user;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}